<template>
  <div class="negotiation-form">
    <div v-if="isLoading" class="negotiation-form__state">
      <spinner size="small" color-scheme="dark" />
      <span>{{ $t('OPPORTUNITY_DETAILS.NEGOTIATION_FORM.LOADING') }}</span>
    </div>

    <p v-else-if="hasLoadError" class="negotiation-form__state">
      {{ $t('OPPORTUNITY_DETAILS.NEGOTIATION_FORM.LOAD_ERROR') }}
    </p>

    <p v-else-if="!isAvailable" class="negotiation-form__state">
      {{ $t('OPPORTUNITY_DETAILS.NEGOTIATION_FORM.UNAVAILABLE') }}
    </p>

    <form v-else class="negotiation-form__body" @submit.prevent="save">
      <div class="negotiation-form__fields">
        <p v-if="readOnlyReason" class="negotiation-form__notice">
          {{ readOnlyReason }}
        </p>
        <p v-else-if="targetStage" class="negotiation-form__notice">
          {{
            $t('OPPORTUNITY_DETAILS.NEGOTIATION_FORM.MOVE_NOTICE', {
              stageName: targetStage.name,
            })
          }}
        </p>

        <opportunity-negotiation-fields
          v-model="form"
          :questions="visibleQuestions"
          :required-question-ids="requiredQuestionIds"
          :errors="errors"
          :disabled="!canEdit || isSaving"
          @field-change="clearError"
        />
      </div>

      <div v-if="canEdit" class="negotiation-form__actions">
        <woot-button :is-loading="isSaving">
          {{ submitLabel }}
        </woot-button>
      </div>
    </form>
  </div>
</template>

<script>
import alertMixin from 'shared/mixins/alertMixin';
import Spinner from 'shared/components/Spinner';
import opportunityWritePermission from 'dashboard/mixins/opportunityWritePermission';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';
import {
  buildAnswersForm,
  buildAnswersPayload,
  buildChangedAnswersPayload,
  invalidAnswerQuestions,
  isQuestionRequiredAt,
  pendingQuestionIdsFromError,
  pendingRequiredQuestions,
} from 'dashboard/helper/opportunityNegotiationFormHelper';
import OpportunityNegotiationFields from './OpportunityNegotiationFields.vue';

export default {
  name: 'OpportunityNegotiationForm',
  components: {
    OpportunityNegotiationFields,
    Spinner,
  },
  mixins: [alertMixin, opportunityWritePermission],
  props: {
    opportunity: {
      type: Object,
      required: true,
    },
    isLocked: {
      type: Boolean,
      default: false,
    },
    pendingQuestionIds: {
      type: Array,
      default: () => [],
    },
    targetStage: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      isLoading: false,
      hasLoadError: false,
      isSaving: false,
      negotiationForm: null,
      form: {},
      savedForm: {},
      errors: {},
      extraQuestionIds: [],
    };
  },
  computed: {
    questions() {
      return (this.negotiationForm && this.negotiationForm.questions) || [];
    },
    isAvailable() {
      return (
        !!this.negotiationForm &&
        this.negotiationForm.status === 'active' &&
        this.questions.length > 0
      );
    },
    canEdit() {
      return !this.isLocked && this.canWriteOpportunities;
    },
    readOnlyReason() {
      if (this.isLocked) {
        return this.$t('OPPORTUNITY_DETAILS.NEGOTIATION_FORM.LOCKED');
      }
      if (!this.canWriteOpportunities) {
        return this.$t('OPPORTUNITY_DETAILS.NEGOTIATION_FORM.READ_ONLY');
      }
      return '';
    },
    requirementStage() {
      return this.targetStage || this.opportunity.pipeline_stage;
    },
    visibleQuestions() {
      if (!this.targetStage) {
        return this.questions;
      }

      const ids = [...this.pendingQuestionIds, ...this.extraQuestionIds];
      const pending = this.questions.filter(question =>
        ids.includes(question.id)
      );
      return pending.length ? pending : this.questions;
    },
    requiredQuestionIds() {
      return this.visibleQuestions
        .filter(question =>
          isQuestionRequiredAt(question, this.requirementStage)
        )
        .map(question => question.id);
    },
    submitLabel() {
      return this.targetStage
        ? this.$t('OPPORTUNITY_DETAILS.NEGOTIATION_FORM.SAVE_AND_MOVE')
        : this.$t('OPPORTUNITY_DETAILS.NEGOTIATION_FORM.SAVE');
    },
  },
  watch: {
    'opportunity.id': {
      immediate: true,
      handler() {
        this.loadNegotiationForm();
      },
    },
    'opportunity.negotiation_form_answers'() {
      if (!this.isSaving) {
        this.rebaseForm();
      }
    },
    pendingQuestionIds(questionIds) {
      if (!this.isLoading && this.negotiationForm) {
        this.showPendingQuestions(questionIds);
      }
    },
  },
  methods: {
    async loadNegotiationForm() {
      const { id: opportunityId, pipeline_id: pipelineId } = this.opportunity;
      this.isLoading = true;
      this.hasLoadError = false;
      try {
        const negotiationForm = await this.$store.dispatch(
          'opportunityPipelines/getNegotiationForm',
          pipelineId
        );
        if (Number(opportunityId) !== Number(this.opportunity.id)) return;

        this.negotiationForm = negotiationForm;
        this.extraQuestionIds = [];
        this.resetForm();
        this.errors = this.targetStage
          ? {}
          : this.pendingErrors(this.pendingQuestionIds);
      } catch (error) {
        this.hasLoadError = true;
      } finally {
        this.isLoading = false;
      }
    },
    resetForm() {
      this.form = buildAnswersForm(
        this.questions,
        this.opportunity.negotiation_form_answers || {}
      );
      this.savedForm = { ...this.form };
    },
    rebaseForm() {
      const typedAnswers = this.form;
      const changedAnswers = buildChangedAnswersPayload(
        this.questions,
        typedAnswers,
        this.savedForm
      );
      this.resetForm();
      Object.keys(changedAnswers).forEach(questionId => {
        this.form[questionId] = typedAnswers[questionId];
      });
    },
    clearError(questionId) {
      this.$delete(this.errors, questionId);
    },
    pendingErrors(questionIds, messageKey = 'PENDING_FOR_MOVE') {
      return questionIds.reduce((errors, id) => {
        errors[id] = this.$t(
          `OPPORTUNITY_DETAILS.NEGOTIATION_FORM.${messageKey}`
        );
        return errors;
      }, {});
    },
    validate(answers) {
      const errors = {};
      invalidAnswerQuestions(this.visibleQuestions, answers).forEach(
        question => {
          errors[question.id] = this.$t(
            `OPPORTUNITY_DETAILS.NEGOTIATION_FORM.INVALID.${question.question_type}`
          );
        }
      );
      pendingRequiredQuestions(
        this.visibleQuestions,
        this.requirementStage,
        answers
      ).forEach(question => {
        errors[question.id] =
          errors[question.id] ||
          this.$t('OPPORTUNITY_DETAILS.NEGOTIATION_FORM.REQUIRED');
      });
      return errors;
    },
    async showPendingQuestions(pendingQuestionIds) {
      const knownIds = this.questions.map(question => question.id);
      if (pendingQuestionIds.some(id => !knownIds.includes(id))) {
        await this.reloadQuestions();
      }
      this.extraQuestionIds = [
        ...new Set([...this.extraQuestionIds, ...pendingQuestionIds]),
      ];
      const savedForm = { ...this.savedForm };
      pendingQuestionIds.forEach(id => delete savedForm[id]);
      this.savedForm = savedForm;
      this.errors = {
        ...this.errors,
        ...this.pendingErrors(
          pendingQuestionIds,
          this.targetStage ? 'PENDING_FOR_MOVE' : 'REQUIRED'
        ),
      };
    },
    async reloadQuestions() {
      const { id: opportunityId, pipeline_id: pipelineId } = this.opportunity;
      const negotiationForm = await this.$store
        .dispatch('opportunityPipelines/getNegotiationForm', pipelineId)
        .catch(() => null);
      if (
        !negotiationForm ||
        Number(opportunityId) !== Number(this.opportunity.id)
      ) {
        return;
      }

      const typedAnswers = this.form;
      this.negotiationForm = negotiationForm;
      this.resetForm();
      this.questions.forEach(question => {
        if (question.id in typedAnswers) {
          this.form[question.id] = typedAnswers[question.id];
        }
      });
    },
    async save() {
      if (!this.canEdit || this.isSaving) return;

      const answers = buildAnswersPayload(this.visibleQuestions, this.form);
      this.errors = this.validate(answers);
      if (Object.keys(this.errors).length) {
        this.showAlert(
          this.$t('OPPORTUNITY_DETAILS.NEGOTIATION_FORM.FIX_ERRORS')
        );
        return;
      }

      this.isSaving = true;
      try {
        const opportunity = await this.$store.dispatch(
          'opportunities/updateNegotiationFormAnswers',
          {
            opportunityId: this.opportunity.id,
            answers: buildChangedAnswersPayload(
              this.visibleQuestions,
              this.form,
              this.savedForm
            ),
          }
        );
        this.savedForm = { ...this.form };
        this.$emit('saved', opportunity);
        if (!this.targetStage) {
          this.showAlert(this.$t('OPPORTUNITY_DETAILS.NEGOTIATION_FORM.SAVED'));
        }
      } catch (error) {
        await this.showPendingQuestions(pendingQuestionIdsFromError(error));
        this.showAlert(
          parseAPIErrorResponse(error) ||
            this.$t('OPPORTUNITY_DETAILS.NEGOTIATION_FORM.SAVE_ERROR')
        );
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.negotiation-form {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
}

.negotiation-form__state {
  align-items: center;
  color: var(--s-700);
  display: flex;
  gap: var(--space-small);
  justify-content: center;
  margin: 0;
  padding: var(--space-large) var(--space-normal);
  text-align: center;
}

.negotiation-form__body {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
}

.negotiation-form__fields {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-normal);
  min-height: 0;
  overflow-y: auto;
  padding: var(--space-normal);
}

.negotiation-form__notice {
  background: var(--s-50);
  border-radius: var(--border-radius-normal);
  color: var(--s-700);
  font-size: var(--font-size-mini);
  margin: 0;
  padding: var(--space-small);
}

.negotiation-form__actions {
  background: var(--white);
  border-top: 1px solid var(--color-border-light);
  display: flex;
  justify-content: flex-end;
  padding: var(--space-small) var(--space-normal);
}
</style>
